import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Chip,
  IconButton,
  Button,
  OutlinedInput,
  Typography,
} from "@mui/material";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import CircularProgress from "@mui/material/CircularProgress";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

// API Configuration - OR CUBE External API
const API_BASE = "https://ai.orcube.com/client/v1";
const CLIENT_ID = "c38fd502-daa4-4c46-a385-71f2fc08ab5c";
const CLIENT_SECRET = "7df2243bdbcc5d735675160fad3a1be3fcdface1e98cb3806dcb661aaaa67cca";

// API Helper
const apiRequest = async (endpoint, method = "GET", body = null) => {
  const options = {
    method,
    headers: {
      "Content-Type": "application/json",
      "X-Client-ID": CLIENT_ID,
      "X-Client-Secret": CLIENT_SECRET,
    },
  };
  if (body) {
    options.body = JSON.stringify(body);
  }
  const response = await fetch(`${API_BASE}${endpoint}`, options);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  if (response.status === 204) return null;
  return response.json();
};

const EditIntent = () => {
  const { intentId } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", type: "success" });

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    intent_category: "simple_response",
    examples: [],
    customResponse: "",
    apiUrl: "",
    workflowId: "",
  });
  const [exampleInput, setExampleInput] = useState("");

  const formShellSx = {
    p: 3,
    borderRadius: 3,
    border: "1px solid #dce3ee",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
  };

  const fieldLabelSx = {
    color: "#344767",
    fontWeight: 700,
    fontSize: "0.9rem",
    mb: 0.65,
    display: "block",
  };

  const vendorInputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      minHeight: 46,
      backgroundColor: "#fff",
      transition: "border-color .2s ease, box-shadow .2s ease",
      "& input": {
        width: "100%",
        padding: "10px 12px",
      },
      "& fieldset": {
        borderColor: "#162b52",
      },
      "&:hover fieldset": {
        borderColor: "#0f2142",
      },
      "&.Mui-focused": {
        boxShadow: "0 0 0 2px rgba(11, 58, 110, 0.16)",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#0b3a6e !important",
        borderWidth: "2px !important",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0b3a6e",
        borderWidth: 2,
      },
    },
  };

  const vendorSelectSx = {
    borderRadius: "10px",
    backgroundColor: "#fff",
    transition: "border-color .2s ease, box-shadow .2s ease",
    "& .MuiSelect-select": {
      py: "12px",
      fontSize: "14px",
      cursor: "pointer",
    },
    "& fieldset": {
      borderColor: "#c7cedb",
    },
    "&:hover fieldset": {
      borderColor: "#97a3ba",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 2px rgba(11, 58, 110, 0.16)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#0b3a6e !important",
      borderWidth: "2px !important",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0b3a6e",
      borderWidth: 2,
    },
  };

  // Fetch intent by ID
  useEffect(() => {
    const fetchIntent = async () => {
      setIsLoading(true);
      try {
        const data = await apiRequest(`/intents/${intentId}`);

        // Extract response config values based on category
        let customResponse = "";
        let apiUrl = "";
        let workflowId = "";

        if (data.intent_category === "simple_response" && data.response_config) {
          customResponse = data.response_config.custom_response || "";
        } else if (data.intent_category === "external_api" && data.response_config) {
          apiUrl = data.response_config.data_url || "";
        } else if (data.intent_category === "workflow" && data.response_config) {
          workflowId = data.response_config.workflow_trigger_id?.toString() || "";
        }

        setFormData({
          name: data.name || "",
          description: data.description || "",
          intent_category: data.intent_category || "simple_response",
          examples: data.examples || [],
          customResponse,
          apiUrl,
          workflowId,
        });
      } catch (error) {
        console.error("Error fetching intent:", error);
        showAlert(`Error loading intent: ${error.message}`, "error");
      }
      setIsLoading(false);
    };

    if (intentId) {
      fetchIntent();
    }
  }, [intentId]);

  const showAlert = (message, type = "success") => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: "", type: "success" }), 5000);
  };

  // Handle form change
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Add example
  const addExample = () => {
    if (exampleInput.trim() && !formData.examples.includes(exampleInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        examples: [...prev.examples, exampleInput.trim()],
      }));
      setExampleInput("");
    }
  };

  // Remove example
  const removeExample = (index) => {
    setFormData((prev) => ({
      ...prev,
      examples: prev.examples.filter((_, i) => i !== index),
    }));
  };

  // Get response config from form
  const getResponseConfig = () => {
    const category = formData.intent_category;
    if (category === "simple_response") {
      return { custom_response: formData.customResponse || "" };
    } else if (category === "external_api") {
      return {
        data_url: formData.apiUrl || "",
        headers: {},
        body: {},
      };
    } else if (category === "workflow") {
      return { workflow_trigger_id: parseInt(formData.workflowId) || null };
    }
    return null;
  };

  // Submit form (update intent)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showAlert("Intent name is required", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        matching_mode: "example",
        examples: formData.examples,
        intent_category: formData.intent_category,
        response_config: getResponseConfig(),
      };

      await apiRequest(`/intents/${intentId}`, "PUT", payload);
      showAlert("Intent updated successfully!", "success");

      // Navigate back after short delay
      setTimeout(() => {
        navigate("/ai-intents");
      }, 1500);
    } catch (error) {
      showAlert(`Error: ${error.message}`, "error");
    }
    setIsSubmitting(false);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <SoftBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress color="info" />
        </SoftBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <SoftBox py={3}>
        {/* Page Header */}
        <SoftBox mb={3} display="flex" alignItems="center" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/ai-intents")}
            sx={{
              borderRadius: 2.5,
              textTransform: "none",
              borderColor: "#cbd5e1",
              color: "#64748b",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "#0ea5e9",
                color: "#0f172a",
                backgroundColor: "#f0f9ff",
                transform: "translateY(-1px)",
              },
            }}
          >
            Back
          </Button>
          <Box>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">
              Edit Intent
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              Modify the intent configuration
            </SoftTypography>
          </Box>
        </SoftBox>

        {/* Alert */}
        {alert.show && (
          <Box
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2,
              backgroundColor: alert.type === "success" ? "#dcfce7" : "#fee2e2",
              color: alert.type === "success" ? "#166534" : "#991b1b",
              border: `1px solid ${alert.type === "success" ? "#bbf7d0" : "#fecaca"}`,
            }}
          >
            {alert.message}
          </Box>
        )}

        <Grid container spacing={3}>
          {/* Edit Intent Form */}
          <Grid item xs={12} lg={8}>
            <Card sx={formShellSx}>
              <SoftTypography variant="h5" fontWeight="bold" mb={3}>
                Intent Details
              </SoftTypography>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <SoftBox mb={1.5}>
                      <SoftTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        sx={fieldLabelSx}
                      >
                        Intent Name *
                      </SoftTypography>
                      <TextField
                        fullWidth
                        placeholder="e.g. Product FAQs"
                        value={formData.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        InputProps={{
                          sx: {
                            padding: "8px 14px !important", // Adjust padding to ensure text visibility
                            "& input": {
                              width: "calc(100% - 0px) !important", // Adjust input width to prevent text cutoff
                            },
                            "& fieldset": {
                              borderColor: "#162b52",
                            },
                            "&:hover fieldset": {
                              borderColor: "#0f2142",
                            },
                            "&.Mui-focused": {
                              boxShadow: "0 0 0 2px rgba(11, 58, 110, 0.16)",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#0b3a6e !important",
                              borderWidth: "2px !important",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#0b3a6e",
                              borderWidth: 2,
                            },
                          },
                        }}
                      />
                    </SoftBox>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SoftBox mb={1.5}>
                      <SoftTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        sx={fieldLabelSx}
                      >
                        Intent Category *
                      </SoftTypography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={formData.intent_category}
                          onChange={(e) => handleChange("intent_category", e.target.value)}
                          input={<OutlinedInput />}
                          sx={vendorSelectSx}
                          renderValue={() => (
                            <Typography
                              variant="body1"
                              style={{ fontSize: "14px", fontWeight: "normal" }}
                            >
                              Simple Response
                            </Typography>
                          )}
                        >
                          <MenuItem value="simple_response">Simple Response</MenuItem>
                        </Select>
                      </FormControl>
                    </SoftBox>
                  </Grid>
                  <Grid item xs={12}>
                    <SoftBox mb={1.5}>
                      <SoftTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        sx={fieldLabelSx}
                      >
                        Description
                      </SoftTypography>
                      <TextField
                        fullWidth
                        placeholder="Optional description"
                        value={formData.description}
                        onChange={(e) => handleChange("description", e.target.value)}
                        InputProps={{
                          sx: {
                            padding: "8px 14px !important", // Adjust padding to ensure text visibility
                            "& input": {
                              width: "calc(100% - 0px) !important", // Adjust input width to prevent text cutoff
                            },
                            "& fieldset": {
                              borderColor: "#162b52",
                            },
                            "&:hover fieldset": {
                              borderColor: "#0f2142",
                            },
                            "&.Mui-focused": {
                              boxShadow: "0 0 0 2px rgba(11, 58, 110, 0.16)",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#0b3a6e !important",
                              borderWidth: "2px !important",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#0b3a6e",
                              borderWidth: 2,
                            },
                          },
                        }}
                      />
                    </SoftBox>
                  </Grid>

                  {/* Examples Input */}
                  <Grid item xs={12}>
                    <SoftTypography variant="caption" fontWeight="medium" mb={1}>
                      Matching Phrases (User phrases that trigger this intent)
                    </SoftTypography>
                    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Type a phrase and press Add"
                        value={exampleInput}
                        onChange={(e) => setExampleInput(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addExample())}
                        InputProps={{
                          sx: {
                            padding: "8px 14px !important", // Adjust padding to ensure text visibility
                            "& input": {
                              width: "calc(100% - 0px) !important", // Adjust input width to prevent text cutoff
                            },
                            "& fieldset": {
                              borderColor: "#162b52",
                            },
                            "&:hover fieldset": {
                              borderColor: "#0f2142",
                            },
                            "&.Mui-focused": {
                              boxShadow: "0 0 0 2px rgba(11, 58, 110, 0.16)",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#0b3a6e !important",
                              borderWidth: "2px !important",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#0b3a6e",
                              borderWidth: 2,
                            },
                          },
                        }}
                      />
                      <IconButton
                        onClick={addExample}
                        sx={{
                          width: 46,
                          height: 46,
                          background: "linear-gradient(135deg, #1f9bf0 0%, #3071af 100%)",
                          color: "#fff",
                          boxShadow: "0 10px 20px rgba(15, 116, 209, 0.28)",
                          transition: "all 0.22s ease",
                          "&:hover": {
                            transform: "translateY(-1px)",
                            background: "linear-gradient(135deg, #1f9bf0 0%, #1f5f9b 100%)",
                          },
                          "&:active": {
                            transform: "scale(0.97)",
                          },
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {formData.examples.map((ex, i) => (
                        <Chip
                          key={i}
                          label={ex}
                          onDelete={() => removeExample(i)}
                          sx={{ backgroundColor: "#e0f2fe", color: "#0369a1", fontWeight: 500 }}
                        />
                      ))}
                    </Box>
                  </Grid>

                  {/* Response Config - Only Simple Response */}
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        backgroundColor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        p: 2.2,
                        borderRadius: 3,
                      }}
                    >
                      <SoftTypography variant="caption" fontWeight="bold" mb={2}>
                        Response Configuration
                      </SoftTypography>

                      <SoftTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        sx={fieldLabelSx}
                      >
                        Custom Response
                      </SoftTypography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Enter the response the AI should give..."
                        value={formData.customResponse || ""}
                        onChange={(e) => handleChange("customResponse", e.target.value)}
                        size="small"
                        sx={{
                          ...vendorInputSx,
                          "& .MuiOutlinedInput-root": {
                            ...vendorInputSx["& .MuiOutlinedInput-root"],
                            alignItems: "flex-start",
                            minHeight: 120,
                          },
                          "& .MuiInputBase-inputMultiline": {
                            width: "100% !important",
                            boxSizing: "border-box",
                            overflow: "auto",
                          },
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        startIcon={
                          isSubmitting ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        sx={{
                          position: "relative",
                          overflow: "hidden",
                          borderRadius: 2.5,
                          background: "linear-gradient(135deg, #1f9bf0 0%, #3071af 100%)",
                          color: "#fff",
                          boxShadow: "0 14px 26px rgba(15, 116, 209, 0.28)",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: "-140%",
                            width: "120%",
                            height: "100%",
                            background:
                              "linear-gradient(110deg, transparent 100%, rgba(255, 255, 255, 0.34) 0%, transparent 100%)",
                            transition: "left 0.55s ease",
                          },
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 18px 28px rgba(15, 116, 209, 0.35)",
                          },
                          "&:hover::before": {
                            left: "130%",
                          },
                          "&:active": {
                            transform: "scale(0.98)",
                          },
                          textTransform: "none",
                          px: 3.2,
                          py: 1.05,
                          fontWeight: 600,
                        }}
                      >
                        {isSubmitting ? "Saving..." : "Save Changes"}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => navigate("/ai-intents")}
                        sx={{
                          borderRadius: 2.5,
                          textTransform: "none",
                          borderColor: "#cbd5e1",
                          color: "#64748b",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: "#0ea5e9",
                            color: "#0f172a",
                            backgroundColor: "#f0f9ff",
                          },
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </Card>
          </Grid>

          {/* Info Panel */}
          <Grid item xs={12} lg={4}>
            <Card
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                backgroundColor: "#f8fafc",
              }}
            >
              <SoftTypography variant="h6" fontWeight="bold" mb={2}>
                💡 Tips
              </SoftTypography>
              <SoftTypography variant="body2" color="text" sx={{ mb: 2 }}>
                <strong>Intent Name:</strong> Use a clear, descriptive name that reflects what this
                intent handles.
              </SoftTypography>
              <SoftTypography variant="body2" color="text" sx={{ mb: 2 }}>
                <strong>Matching Phrases:</strong> Add multiple variations of how users might
                express this intent. More examples = better matching.
              </SoftTypography>
              <SoftTypography variant="body2" color="text">
                <strong>Response Config:</strong> Configure how the AI should respond when this
                intent is detected.
              </SoftTypography>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
};

export default EditIntent;
